import { Link } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useTenantStore } from '@/stores/tenantStore';
import { buildPublicTableUrl, useTenantHref } from '@/lib/urls';
import {
  listTables,
  createTable,
  regenTableQr,
  updateTable,
  deleteTable,
  releaseTable,
  type RestaurantTable,
  type UpdateTableOptions,
  type FetchTablesResponse,
  type TableActivitySummaryResponse,
} from '@/lib/api/tables';
import { createTenantLocation, fetchTenantLocations, type TenantLocationRecord } from '@/lib/api/settings';
import { fetchQrMetrics, type QrMetricsResponse } from '@/lib/api/qr';
import { useLocaleFormatter } from '@/lib/i18n/useLocaleFormatter';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';
import { QrOrderingStatusCard } from '@/features/restaurant/components/qr/QrOrderingStatusCard';
import {
  AlertCircle,
  AlertTriangle,
  ArrowUpRight,
  BarChart3,
  Calendar,
  Check,
  CheckCircle2,
  Copy,
  DollarSign,
  Download,
  Eye,
  Filter,
  Printer,
  QrCode,
  RefreshCcw,
  Search,
  TrendingUp,
  Users,
  Plus,
  Pencil,
  BadgeCheck,
  Loader2,
  ExternalLink,
  Wifi,
  WifiOff,
  SlidersHorizontal,
  ListPlus,
  Trash2,
  PauseCircle,
  Power,
} from 'lucide-react';

type GeneratedAssets = {
  pngDataUrl: string;
  deepLink: string;
  svg?: string; // optional; PNG-only flow supported
};

type DashboardOrderLike = Record<string, unknown> & {
  table_id?: string | null;
  status?: string;
};

const isDashboardOrderLike = (value: unknown): value is DashboardOrderLike => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  return 'table_id' in value;
};

const PRESET_LOCATION_VALUE_PREFIX = 'preset:';
const PRESET_LOCATION_NAMES = ['Pick Up', 'Indoor', 'Outdoor', 'Patio'] as const;
type PresetLocationName = (typeof PRESET_LOCATION_NAMES)[number];
const PRESET_LOCATION_NAME_SET = new Set(PRESET_LOCATION_NAMES.map((name) => name.toLowerCase()));
const EMPTY_TABLES: RestaurantTable[] = [];
const EMPTY_LOCATIONS: TenantLocationRecord[] = [];

const isPickupLocation = (location?: string | null): boolean =>
  (location ?? '').trim().toLowerCase() === 'pick up';

const toSafeFileStem = (value: string | number | null | undefined): string => {
  const raw = value === undefined || value === null ? '' : String(value);
  const normalized = raw.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return normalized || 'table';
};

const getTableDisplayMetadata = (table: RestaurantTable) => {
  const pickup = isPickupLocation(table.location);
  const originalLabel = table.tableNumber ?? table.name ?? table.qrCodeSlug;
  const fallbackLabel = table.qrCodeSlug || 'Table';
  const resolvedLabel = originalLabel ?? fallbackLabel;

  return {
    isPickup: pickup,
    displayLabel: pickup ? 'N/A' : String(resolvedLabel),
    fileStem: pickup ? 'pickup' : toSafeFileStem(resolvedLabel),
  };
};

const statusVisuals = (
  tableStatus: RestaurantTable['status'] | undefined,
  orderingStatus: TenantLocationRecord['qrOrderingStatus'] | null | undefined,
  t: TFunction<'dashboard'>,
) => {
  if (orderingStatus === 'paused') {
    return {
      label: t('dashboard:qrOrdering.status.paused', { defaultValue: 'Paused' }),
      badgeClass:
        'border border-amber-500/30 bg-amber-500/10 text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300',
      description: t('dashboard:qrCodes.status.paused.description', {
        defaultValue: 'QR ordering is paused; guide guests to order at the counter.',
      }),
      icon: PauseCircle,
    };
  }

  if (orderingStatus === 'stopped') {
    return {
      label: t('dashboard:qrOrdering.status.stopped', { defaultValue: 'Stopped' }),
      badgeClass:
        'border border-destructive/50 bg-destructive/10 text-destructive dark:border-destructive/40 dark:bg-destructive/20 dark:text-destructive-foreground',
      description: t('dashboard:qrCodes.status.stopped.description', {
        defaultValue: 'QR ordering is offline for this area.',
      }),
      icon: Power,
    };
  }

  switch (tableStatus) {
    case 'OCCUPIED':
      return {
        label: t('dashboard:qrCodes.status.occupied.label', { defaultValue: 'In service' }),
        badgeClass: 'border border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-300',
        description: t('dashboard:qrCodes.status.occupied.description', {
          defaultValue: 'Guests are currently seated',
        }),
        icon: Users,
      };
    case 'RESERVED':
      return {
        label: t('dashboard:qrCodes.status.reserved.label', { defaultValue: 'Reserved' }),
        badgeClass: 'border border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300',
        description: t('dashboard:qrCodes.status.reserved.description', {
          defaultValue: 'Upcoming reservation',
        }),
        icon: Calendar,
      };
    case 'OPEN':
    default:
      return {
        label: t('dashboard:qrCodes.status.available.label', { defaultValue: 'Available' }),
        badgeClass:
          'border border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
        description: t('dashboard:qrCodes.status.available.description', {
          defaultValue: 'Ready for guests',
        }),
        icon: CheckCircle2,
      };
  }
};

const toErrorMessage = (err: unknown, fallback = 'Failed to load QR codes'): string => {
  if (!err) {
    return fallback;
  }
  if (typeof err === 'string') {
    return err;
  }
  if (err instanceof Error) {
    return err.message || fallback;
  }
  if (typeof err === 'object') {
    const maybeWithMessage = err as { message?: unknown };
    if (typeof maybeWithMessage.message === 'string') {
      return maybeWithMessage.message;
    }
    const maybeResponse = err as { response?: { data?: { error?: unknown } } };
    const responseError = maybeResponse.response?.data?.error;
    if (typeof responseError === 'string') {
      return responseError;
    }
  }
  return fallback;
};

type BulkTableEntry = {
  id: string;
  locationValue: string;
  locationName: string;
  lastManualStartNumber: string;
  startNumber: string;
  count: string;
};

const createBulkTableEntry = (): BulkTableEntry => ({
  id: `bulk-${Math.random().toString(36).slice(2, 8)}-${Date.now().toString(36)}`,
  locationValue: '__none',
  locationName: '',
  lastManualStartNumber: '',
  startNumber: '',
  count: '1',
});

const QRCodesPageV3 = () => {
  const { t } = useTranslation(['dashboard']);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const activeRestaurantId = useTenantStore((s) => s.activeRestaurantId);
  const activeSubdomain = useTenantStore((s) => s.activeSubdomain);
  const restaurantName = useTenantStore((s) => s.activeRestaurantName) ?? 'Your Restaurant';
  const backHref = useTenantHref('dashboard');
  const isRestaurantSelected = Boolean(activeRestaurantId);

  const tablesQuery = useQuery({
    queryKey: ['tables', { restaurantId: activeRestaurantId }],
    queryFn: ({ signal }) => {
      if (!activeRestaurantId) return Promise.resolve({ tables: [] });
      return listTables(activeRestaurantId, signal);
    },
    enabled: isRestaurantSelected,
  });

  const errorMessage = useMemo(() => toErrorMessage(tablesQuery.error), [tablesQuery.error]);
  const tablesData = tablesQuery.data?.tables;
  const tables = useMemo(() => tablesData ?? EMPTY_TABLES, [tablesData]);

  const [generatedAssets, setGeneratedAssets] = useState<Record<string, GeneratedAssets>>({});
  const [copiedTableId, setCopiedTableId] = useState<string | null>(null);
  const [generatingTableId, setGeneratingTableId] = useState<string | null>(null);
  const [isBulkGenerating, setIsBulkGenerating] = useState(false);
  const [creatingTable, setCreatingTable] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isBulkCreateDialogOpen, setIsBulkCreateDialogOpen] = useState(false);
  const [isBulkCreatingTables, setIsBulkCreatingTables] = useState(false);
  const [bulkEntries, setBulkEntries] = useState<BulkTableEntry[]>(() => [createBulkTableEntry()]);
  const [bulkFormError, setBulkFormError] = useState<string | null>(null);
  const [tableNumberInput, setTableNumberInput] = useState('');
  const [lastManualTableNumber, setLastManualTableNumber] = useState('');
  const [selectedLocationValue, setSelectedLocationValue] = useState<string>('__none');
  const [selectedLocationName, setSelectedLocationName] = useState<string>('');
  const [customSlugInput, setCustomSlugInput] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [editingTable, setEditingTable] = useState<RestaurantTable | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [updatingTableId, setUpdatingTableId] = useState<string | null>(null);
  const [releasingTableId, setReleasingTableId] = useState<string | null>(null);
  const [editTableNumberInput, setEditTableNumberInput] = useState('');
  const [editOriginalTableNumber, setEditOriginalTableNumber] = useState('');
  const [editLastManualTableNumber, setEditLastManualTableNumber] = useState('');
  const [editSelectedLocationValue, setEditSelectedLocationValue] = useState<string>('__none');
  const [editSelectedLocationName, setEditSelectedLocationName] = useState<string>('');
  const [editLocationInitialised, setEditLocationInitialised] = useState(false);
  const [editCustomSlugInput, setEditCustomSlugInput] = useState('');
  const [editOriginalSlug, setEditOriginalSlug] = useState('');
  const [editFormError, setEditFormError] = useState<string | null>(null);
  const [tablePendingDelete, setTablePendingDelete] = useState<RestaurantTable | null>(null);
  const [deletingTableId, setDeletingTableId] = useState<string | null>(null);

  const locationsQuery = useQuery({
    queryKey: ['settings', 'locations'],
    queryFn: ({ signal }) => fetchTenantLocations(signal).then((response) => response.locations),
    enabled: isRestaurantSelected,
    staleTime: 60_000,
  });
  const locationsData = locationsQuery.data;
  const availableLocations = useMemo(
    () => locationsData ?? EMPTY_LOCATIONS,
    [locationsData],
  );

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'available' | 'reserved' | 'occupied'>('all');

  const presetLocationOptions = useMemo(
    () => {
      const lookup = new Map<string, TenantLocationRecord>();
      for (const location of availableLocations) {
        lookup.set(location.name.trim().toLowerCase(), location);
      }
      return PRESET_LOCATION_NAMES.map((name) => {
        const existing = lookup.get(name.toLowerCase());
        if (existing) {
          return { value: existing.id, label: existing.name };
        }
        return { value: `${PRESET_LOCATION_VALUE_PREFIX}${name}`, label: name };
      });
    },
    [availableLocations]
  );

  const additionalLocations = useMemo(
    () =>
      availableLocations
        .filter((location) => !PRESET_LOCATION_NAME_SET.has(location.name.trim().toLowerCase()))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [availableLocations]
  );

  const locationOrderingById = useMemo(() => {
    const map = new Map<string, TenantLocationRecord['qrOrderingStatus']>();
    for (const location of availableLocations) {
      if (location.id) {
        map.set(location.id, location.qrOrderingStatus);
      }
    }
    return map;
  }, [availableLocations]);

  const locationOrderingByName = useMemo(() => {
    const map = new Map<string, TenantLocationRecord['qrOrderingStatus']>();
    for (const location of availableLocations) {
      const normalized = location.name.trim().toLowerCase();
      if (normalized) {
        map.set(normalized, location.qrOrderingStatus);
      }
    }
    return map;
  }, [availableLocations]);

  const ensureLocationByName = useCallback(
    async (name: string): Promise<TenantLocationRecord> => {
      const normalized = name.trim();
      if (!normalized) {
        throw new Error('Location name is required');
      }

      const existing = availableLocations.find(
        (location) => location.name.trim().toLowerCase() === normalized.toLowerCase()
      );
      if (existing) {
        return existing;
      }

      const template = availableLocations[0];
      const response = await createTenantLocation({
        name: normalized,
        timezone: template?.timezone ?? 'UTC',
        currency: template?.currency ?? 'USD',
        defaultTaxRateBps: template?.defaultTaxRateBps ?? 0,
        defaultServiceChargeBps: template?.defaultServiceChargeBps ?? 0,
        defaultCardSurchargeBps: template?.defaultCardSurchargeBps ?? 0,
      });

      queryClient.setQueryData<TenantLocationRecord[] | undefined>(
        ['settings', 'locations'],
        (previous) => {
          const current = previous ?? availableLocations;
          if (current.some((location) => location.id === response.location.id)) {
            return current;
          }
          return [...current, response.location].sort((a, b) => a.name.localeCompare(b.name));
        }
      );

      await queryClient.invalidateQueries({ queryKey: ['settings', 'locations'] });

      return response.location;
    },
    [availableLocations, queryClient]
  );

  const getLocationRecordForSelection = useCallback(
    async (value: string, name: string): Promise<TenantLocationRecord | null> => {
      if (!value || value === '__none') {
        return null;
      }

      const matchById = availableLocations.find((location) => location.id === value);
      if (matchById) {
        return matchById;
      }

      const normalizedName = name.trim();
      if (!normalizedName) {
        return null;
      }

      return ensureLocationByName(normalizedName);
    },
    [availableLocations, ensureLocationByName]
  );

  const handleLocationChange = useCallback(
    (value: string) => {
      if (value === '__none') {
        setSelectedLocationValue(value);
        setSelectedLocationName('');
        if (tableNumberInput === 'N/A') {
          setTableNumberInput(lastManualTableNumber || '');
        }
        return;
      }

      if (value.startsWith(PRESET_LOCATION_VALUE_PREFIX)) {
        const presetName = value.slice(PRESET_LOCATION_VALUE_PREFIX.length) as PresetLocationName;
        setSelectedLocationValue(value);
        setSelectedLocationName(presetName);
        if (presetName.toLowerCase() === 'pick up') {
          if (tableNumberInput !== 'N/A') {
            setLastManualTableNumber(tableNumberInput);
          }
          setTableNumberInput('N/A');
        } else if (tableNumberInput === 'N/A') {
          setTableNumberInput(lastManualTableNumber || '');
        }
        return;
      }

      const existing = availableLocations.find((location) => location.id === value);
      setSelectedLocationValue(value);
      setSelectedLocationName(existing?.name ?? '');
      if (isPickupLocation(existing?.name)) {
        if (tableNumberInput !== 'N/A') {
          setLastManualTableNumber(tableNumberInput);
        }
        setTableNumberInput('N/A');
      } else if (tableNumberInput === 'N/A') {
        setTableNumberInput(lastManualTableNumber || '');
      }
    },
    [availableLocations, lastManualTableNumber, tableNumberInput]
  );

  const handleBulkLocationChange = useCallback(
    (entryId: string, value: string) => {
      setBulkFormError(null);
      setBulkEntries((entries) =>
        entries.map((entry) => {
          if (entry.id !== entryId) {
            return entry;
          }

          if (value === '__none') {
            const restored =
              entry.startNumber && entry.startNumber !== 'N/A'
                ? entry.startNumber
                : entry.lastManualStartNumber;
            return {
              ...entry,
              locationValue: value,
              locationName: '',
              startNumber: restored ?? '',
            };
          }

          if (value.startsWith(PRESET_LOCATION_VALUE_PREFIX)) {
            const presetName = value.slice(PRESET_LOCATION_VALUE_PREFIX.length) as PresetLocationName;
            if (presetName.toLowerCase() === 'pick up') {
              return {
                ...entry,
                locationValue: value,
                locationName: presetName,
                lastManualStartNumber: entry.startNumber || entry.lastManualStartNumber,
                startNumber: 'N/A',
              };
            }
            const restored =
              entry.startNumber && entry.startNumber !== 'N/A'
                ? entry.startNumber
                : entry.lastManualStartNumber;
            return {
              ...entry,
              locationValue: value,
              locationName: presetName,
              startNumber: restored ?? '',
            };
          }

          const existing = availableLocations.find((location) => location.id === value);
          if (!existing) {
            return {
              ...entry,
              locationValue: value,
            };
          }

          if (isPickupLocation(existing.name)) {
            return {
              ...entry,
              locationValue: value,
              locationName: existing.name,
              lastManualStartNumber: entry.startNumber || entry.lastManualStartNumber,
              startNumber: 'N/A',
            };
          }

          const restored =
            entry.startNumber && entry.startNumber !== 'N/A'
              ? entry.startNumber
              : entry.lastManualStartNumber;

          return {
            ...entry,
            locationValue: value,
            locationName: existing.name,
            startNumber: restored ?? '',
          };
        })
      );
    },
    [availableLocations]
  );

  const handleBulkFieldChange = useCallback((entryId: string, field: 'startNumber' | 'count', value: string) => {
    setBulkFormError(null);
    setBulkEntries((entries) =>
      entries.map((entry) => {
        if (entry.id !== entryId) {
          return entry;
        }
        if (field === 'startNumber') {
          return {
            ...entry,
            startNumber: value,
            lastManualStartNumber: value && value !== 'N/A' ? value : entry.lastManualStartNumber,
          };
        }
        return {
          ...entry,
          count: value,
        };
      })
    );
  }, []);

  const handleAddBulkEntry = useCallback(() => {
    setBulkFormError(null);
    setBulkEntries((entries) => [...entries, createBulkTableEntry()]);
  }, []);

  const handleRemoveBulkEntry = useCallback((entryId: string) => {
    setBulkFormError(null);
    setBulkEntries((entries) => {
      if (entries.length <= 1) {
        return entries;
      }
      return entries.filter((entry) => entry.id !== entryId);
    });
  }, []);

  const isPickUpSelected = isPickupLocation(selectedLocationName);
  const handleEditLocationChange = useCallback(
    (value: string) => {
      setEditLocationInitialised(true);

      if (value === '__none') {
        setEditSelectedLocationValue(value);
        setEditSelectedLocationName('');
        if (editTableNumberInput === 'N/A') {
          const fallback = editLastManualTableNumber || editOriginalTableNumber;
          setEditTableNumberInput(fallback);
        }
        return;
      }

      if (value.startsWith(PRESET_LOCATION_VALUE_PREFIX)) {
        const presetName = value.slice(PRESET_LOCATION_VALUE_PREFIX.length) as PresetLocationName;
        setEditSelectedLocationValue(value);
        setEditSelectedLocationName(presetName);
        if (presetName.toLowerCase() === 'pick up') {
          if (editTableNumberInput !== 'N/A') {
            setEditLastManualTableNumber(editTableNumberInput);
          }
          setEditTableNumberInput('N/A');
        } else if (editTableNumberInput === 'N/A') {
          const fallback = editLastManualTableNumber || editOriginalTableNumber;
          setEditTableNumberInput(fallback);
        }
        return;
      }

      const existing = availableLocations.find((location) => location.id === value);
      setEditSelectedLocationValue(value);
      setEditSelectedLocationName(existing?.name ?? '');
      if (isPickupLocation(existing?.name)) {
        if (editTableNumberInput !== 'N/A') {
          setEditLastManualTableNumber(editTableNumberInput);
        }
        setEditTableNumberInput('N/A');
      } else if (editTableNumberInput === 'N/A') {
        const fallback = editLastManualTableNumber || editOriginalTableNumber;
        setEditTableNumberInput(fallback);
      }
    },
    [availableLocations, editLastManualTableNumber, editOriginalTableNumber, editTableNumberInput]
  );
  const editIsPickUpSelected = isPickupLocation(editSelectedLocationName);

  useEffect(() => {
    if (!selectedLocationValue.startsWith(PRESET_LOCATION_VALUE_PREFIX)) {
      return;
    }
    const presetName = selectedLocationValue.slice(PRESET_LOCATION_VALUE_PREFIX.length).toLowerCase();
    const resolved = availableLocations.find(
      (location) => location.name.trim().toLowerCase() === presetName
    );
    if (resolved) {
      setSelectedLocationValue(resolved.id);
      setSelectedLocationName(resolved.name);
      if (isPickupLocation(resolved.name)) {
        if (tableNumberInput !== 'N/A') {
          setLastManualTableNumber(tableNumberInput);
        }
        setTableNumberInput('N/A');
      }
    }
  }, [availableLocations, selectedLocationValue, tableNumberInput]);

  useEffect(() => {
    if (!isEditDialogOpen || editLocationInitialised) {
      return;
    }

    if (!editingTable) {
      setEditSelectedLocationValue('__none');
      setEditSelectedLocationName('');
      setEditLocationInitialised(true);
      if (editTableNumberInput === 'N/A') {
        const fallback = editLastManualTableNumber || editOriginalTableNumber;
        setEditTableNumberInput(fallback);
      }
      return;
    }

    const locationName = editingTable.location?.trim();
    if (!locationName) {
      setEditSelectedLocationValue('__none');
      setEditSelectedLocationName('');
      setEditLocationInitialised(true);
      if (editTableNumberInput === 'N/A') {
        const fallback = editLastManualTableNumber || editOriginalTableNumber;
        setEditTableNumberInput(fallback);
      }
      return;
    }

    const presetMatch = PRESET_LOCATION_NAMES.find(
      (name) => name.toLowerCase() === locationName.toLowerCase()
    );
    if (presetMatch) {
      setEditSelectedLocationValue(`${PRESET_LOCATION_VALUE_PREFIX}${presetMatch}`);
      setEditSelectedLocationName(presetMatch);
      setEditLocationInitialised(true);
      return;
    }

    const existing = availableLocations.find(
      (location) => location.name.trim().toLowerCase() === locationName.toLowerCase()
    );

    if (existing) {
      setEditSelectedLocationValue(existing.id);
      setEditSelectedLocationName(existing.name);
      setEditLocationInitialised(true);
      if (isPickupLocation(existing.name)) {
        if (editTableNumberInput !== 'N/A') {
          setEditLastManualTableNumber(editTableNumberInput);
        }
        setEditTableNumberInput('N/A');
      }
      return;
    }

    setEditSelectedLocationValue('__none');
    setEditSelectedLocationName(locationName);
    setEditLocationInitialised(true);
  }, [
    availableLocations,
    editLastManualTableNumber,
    editLocationInitialised,
    editOriginalTableNumber,
    editTableNumberInput,
    editingTable,
    isEditDialogOpen,
  ]);

  useEffect(() => {
    if (!isEditDialogOpen) {
      return;
    }
    if (!editSelectedLocationValue.startsWith(PRESET_LOCATION_VALUE_PREFIX)) {
      return;
    }
    const presetName = editSelectedLocationValue.slice(PRESET_LOCATION_VALUE_PREFIX.length).toLowerCase();
    const resolved = availableLocations.find(
      (location) => location.name.trim().toLowerCase() === presetName
    );
    if (resolved) {
      setEditSelectedLocationValue(resolved.id);
      setEditSelectedLocationName(resolved.name);
      setEditLocationInitialised(true);
      if (isPickupLocation(resolved.name)) {
        if (editTableNumberInput !== 'N/A') {
          setEditLastManualTableNumber(editTableNumberInput);
        }
        setEditTableNumberInput('N/A');
      } else if (editTableNumberInput === 'N/A') {
        const fallback = editLastManualTableNumber || editOriginalTableNumber;
        setEditTableNumberInput(fallback);
      }
    }
  }, [
    availableLocations,
    editLastManualTableNumber,
    editOriginalTableNumber,
    editSelectedLocationValue,
    editTableNumberInput,
    isEditDialogOpen,
  ]);

  const qrMetricsQuery = useQuery({
    queryKey: ['qr-metrics', { restaurantId: activeRestaurantId, tenant: activeSubdomain }],
    queryFn: ({ signal }) => {
      if (!activeSubdomain) {
        return Promise.resolve(null);
      }
      return fetchQrMetrics(activeSubdomain, { signal });
    },
    enabled: isRestaurantSelected && Boolean(activeSubdomain),
  });

  const { formatCurrency, formatNumber } = useLocaleFormatter();
  const currencyCode = 'USD';

  const resetBulkForm = useCallback(() => {
    setBulkEntries([createBulkTableEntry()]);
    setBulkFormError(null);
  }, []);

  const resetCreateForm = useCallback(() => {
    setTableNumberInput('');
    setLastManualTableNumber('');
    setSelectedLocationValue('__none');
    setSelectedLocationName('');
    setCustomSlugInput('');
    setFormError(null);
  }, []);

  const resetEditForm = useCallback(() => {
    setEditingTable(null);
    setUpdatingTableId(null);
    setEditTableNumberInput('');
    setEditOriginalTableNumber('');
    setEditLastManualTableNumber('');
    setEditSelectedLocationValue('__none');
    setEditSelectedLocationName('');
    setEditLocationInitialised(false);
    setEditCustomSlugInput('');
    setEditOriginalSlug('');
    setEditFormError(null);
  }, []);

  useEffect(() => {
    if (!isBulkCreateDialogOpen) {
      resetBulkForm();
      setIsBulkCreatingTables(false);
    }
  }, [isBulkCreateDialogOpen, resetBulkForm]);

  useEffect(() => {
    if (!isCreateDialogOpen) {
      resetCreateForm();
    }
  }, [isCreateDialogOpen, resetCreateForm]);

  useEffect(() => {
    if (!isEditDialogOpen) {
      resetEditForm();
    }
  }, [isEditDialogOpen, resetEditForm]);

  const computeDeepLink = useCallback(
    (table: RestaurantTable, options: { forceLocal?: boolean; demo?: boolean } = {}) => {
      const { forceLocal = false, demo = false } = options;
      if (!forceLocal && table.deepLink) return table.deepLink;
      if (!activeSubdomain) return '';
      return buildPublicTableUrl({
        subdomain: activeSubdomain,
        qrCodeSlug: table.qrCodeSlug,
        token: table.deepLinkToken,
        demo,
        forceLocal,
      });
    },
    [activeSubdomain]
  );

  useEffect(() => {
    if (!activeRestaurantId) return;
    const channel = supabase
      .channel(`tables-${activeRestaurantId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'restaurant_tables',
          filter: `restaurant_id=eq.${activeRestaurantId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['tables'] });
          queryClient.invalidateQueries({ queryKey: ['tables', { restaurantId: activeRestaurantId }] });
          if (import.meta.env?.DEV) console.debug('[QRCodesPage] realtime table update received');
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [activeRestaurantId, queryClient]);

  useEffect(() => {
    if (!copiedTableId) return;
    const timeout = window.setTimeout(() => setCopiedTableId(null), 2000);
    return () => window.clearTimeout(timeout);
  }, [copiedTableId]);

  const handleSubmitBulkTables = useCallback(
    async (event?: FormEvent<HTMLFormElement>) => {
      if (event) {
        event.preventDefault();
      }

      setBulkFormError(null);

      if (!bulkEntries.length) {
        setBulkFormError(
          t('dashboard:qrCodes.bulkDialog.errors.noBatches', {
            defaultValue: 'Add at least one batch before creating tables.',
          })
        );
        return;
      }

      const updatedEntries = [...bulkEntries];
      const existingTableNumbers = new Set<number>();
      const pendingTableNumbers = new Set<number>();

      for (const table of tables) {
        if (table.tableNumber !== undefined && table.tableNumber !== null) {
          existingTableNumbers.add(Number(table.tableNumber));
        }
      }

      const operations: Array<{
        tableNumber?: number;
        locationId?: string;
        locationName?: string;
      }> = [];

      for (let index = 0; index < bulkEntries.length; index += 1) {
        const entry = bulkEntries[index];
        const isPickup = isPickupLocation(entry.locationName);
        const countRaw = entry.count.trim();

        if (!countRaw) {
          setBulkFormError(
            t('dashboard:qrCodes.bulkDialog.errors.countRequired', {
              defaultValue: 'Enter how many tables to create in batch {{index}}.',
              index: index + 1,
            })
          );
          return;
        }

        if (!/^\d+$/u.test(countRaw)) {
          setBulkFormError(
            t('dashboard:qrCodes.bulkDialog.errors.countInvalid', {
              defaultValue: 'Batch {{index}} count must be a whole number.',
              index: index + 1,
            })
          );
          return;
        }

        const countValue = Number.parseInt(countRaw, 10);
        if (!Number.isFinite(countValue) || countValue <= 0) {
          setBulkFormError(
            t('dashboard:qrCodes.bulkDialog.errors.countMinimum', {
              defaultValue: 'Batch {{index}} must add at least one table.',
              index: index + 1,
            })
          );
          return;
        }

        if (countValue > 50) {
          setBulkFormError(
            t('dashboard:qrCodes.bulkDialog.errors.countMaximum', {
              defaultValue: 'Batch {{index}} can add at most 50 tables at a time.',
              index: index + 1,
            })
          );
          return;
        }

        let startNumber: number | undefined;

        if (!isPickup) {
          const startRaw = entry.startNumber.trim();
          if (!startRaw) {
            setBulkFormError(
              t('dashboard:qrCodes.bulkDialog.errors.startRequired', {
                defaultValue: 'Provide a starting table number for batch {{index}}.',
                index: index + 1,
              })
            );
            return;
          }

          if (!/^\d+$/u.test(startRaw)) {
            setBulkFormError(
              t('dashboard:qrCodes.bulkDialog.errors.startInvalid', {
                defaultValue: 'Batch {{index}} starting number must be a positive number.',
                index: index + 1,
              })
            );
            return;
          }

          const parsedStart = Number.parseInt(startRaw, 10);
          if (!Number.isFinite(parsedStart) || parsedStart <= 0) {
            setBulkFormError(
              t('dashboard:qrCodes.bulkDialog.errors.startMinimum', {
                defaultValue: 'Batch {{index}} starting number must be greater than zero.',
                index: index + 1,
              })
            );
            return;
          }

          startNumber = parsedStart;
        }

        let resolvedLocationId: string | undefined;
        let resolvedLocationName = entry.locationName;

        if (entry.locationValue !== '__none') {
          try {
            const locationRecord = await getLocationRecordForSelection(entry.locationValue, entry.locationName);
            if (!locationRecord) {
              throw new Error('Unable to resolve selected location');
            }
            resolvedLocationId = locationRecord.id;
            resolvedLocationName = locationRecord.name;
            updatedEntries[index] = {
              ...entry,
              locationValue: locationRecord.id,
              locationName: locationRecord.name,
            };
          } catch (error) {
            console.error('[QRCodesPage] failed to resolve location for bulk table creation', error);
            setBulkFormError(
              t('dashboard:qrCodes.bulkDialog.errors.location', {
                defaultValue: 'We could not resolve the location for batch {{index}}. Try again.',
                index: index + 1,
              })
            );
            return;
          }
        } else if (entry.locationName) {
          updatedEntries[index] = {
            ...entry,
            locationName: '',
          };
        }

        for (let offset = 0; offset < countValue; offset += 1) {
          const tableNumber = startNumber !== undefined ? startNumber + offset : undefined;
          if (tableNumber !== undefined) {
            if (pendingTableNumbers.has(tableNumber) || existingTableNumbers.has(tableNumber)) {
              setBulkFormError(
                t('dashboard:qrCodes.bulkDialog.errors.duplicateNumber', {
                  defaultValue: 'Table number {{tableNumber}} is already in use.',
                  tableNumber,
                })
              );
              return;
            }
            pendingTableNumbers.add(tableNumber);
          }

          operations.push({
            tableNumber,
            locationId: resolvedLocationId,
            locationName: resolvedLocationName,
          });
        }
      }

      if (!operations.length) {
        setBulkFormError(
          t('dashboard:qrCodes.bulkDialog.errors.noTables', {
            defaultValue: 'Nothing to create yet. Update your batches and try again.',
          })
        );
        return;
      }

      try {
        setIsBulkCreatingTables(true);
        if (!activeSubdomain) {
          throw new Error('missing tenant slug');
        }

        for (const operation of operations) {
          await createTable(activeSubdomain, {
            tableNumber: operation.tableNumber,
            locationId: operation.locationId,
          });
        }

        setBulkEntries(updatedEntries);

        await queryClient.invalidateQueries({ queryKey: ['tables'] });
        await queryClient.invalidateQueries({ queryKey: ['tables', { restaurantId: activeRestaurantId }] });
        setIsBulkCreateDialogOpen(false);

        toast({
          title: t('dashboard:qrCodes.bulkDialog.successTitle', { defaultValue: 'Tables created' }),
          description: t('dashboard:qrCodes.bulkDialog.successDescription', {
            defaultValue: 'Created {{count}} tables successfully.',
            count: operations.length,
          }),
        });
      } catch (error) {
        console.error('[QRCodesPage] failed to create tables in bulk', error);
        const message = toErrorMessage(
          error,
          t('dashboard:qrCodes.bulkDialog.errors.generic', { defaultValue: 'Please try again.' })
        );
        setBulkFormError(message);
        toast({
          title: t('dashboard:qrCodes.bulkDialog.errorTitle', { defaultValue: 'Unable to add tables' }),
          description: message,
          variant: 'destructive',
        });
      } finally {
        setIsBulkCreatingTables(false);
      }
    },
    [activeRestaurantId, activeSubdomain, bulkEntries, getLocationRecordForSelection, queryClient, t, tables, toast]
  );

  const handleSubmitNewTable = useCallback(
    async (event?: FormEvent<HTMLFormElement>) => {
      if (event) {
        event.preventDefault();
      }

      setFormError(null);

      const trimmedNumber = tableNumberInput.trim();
      let resolvedTableNumber: number | undefined;

      if (!isPickUpSelected) {
        if (!trimmedNumber) {
          setFormError('Enter a table number to continue.');
          return;
        }

        const parsedNumber = Number.parseInt(trimmedNumber, 10);
        if (!Number.isFinite(parsedNumber) || parsedNumber <= 0) {
          setFormError('Table number must be a positive number.');
          return;
        }
        resolvedTableNumber = parsedNumber;
      }

      try {
        setCreatingTable(true);
        if (!activeSubdomain) throw new Error('missing tenant slug');

        let resolvedLocationId: string | undefined;
        let resolvedLocationName = selectedLocationName;

        if (selectedLocationValue !== '__none') {
          const locationRecord = await getLocationRecordForSelection(
            selectedLocationValue,
            selectedLocationName
          );

          if (!locationRecord) {
            throw new Error('Unable to resolve selected location');
          }

          resolvedLocationId = locationRecord.id;
          resolvedLocationName = locationRecord.name;
          setSelectedLocationValue(locationRecord.id);
          setSelectedLocationName(locationRecord.name);
        }

        await createTable(activeSubdomain, {
          tableNumber: resolvedTableNumber,
          locationId: resolvedLocationId,
          qrCodeSlug: customSlugInput.trim() || undefined,
        });

        await queryClient.invalidateQueries({ queryKey: ['tables'] });
        await queryClient.invalidateQueries({ queryKey: ['tables', { restaurantId: activeRestaurantId }] });
        setIsCreateDialogOpen(false);

        const successDescriptor = isPickUpSelected
          ? 'Pick Up station is ready.'
          : resolvedTableNumber
            ? `Table ${resolvedTableNumber} is ready.`
            : resolvedLocationName
              ? `${resolvedLocationName} table is ready.`
              : 'Table created.';

        toast({ title: 'Table created', description: successDescriptor });
      } catch (error) {
        console.error('[QRCodesPage] failed to create table', error);
        const message = toErrorMessage(error, 'Please try again.');
        setFormError(message);
        toast({ title: 'Unable to add table', description: message, variant: 'destructive' });
      } finally {
        setCreatingTable(false);
      }
    },
    [
      activeRestaurantId,
      activeSubdomain,
      customSlugInput,
      getLocationRecordForSelection,
      isPickUpSelected,
      queryClient,
      selectedLocationName,
      selectedLocationValue,
      tableNumberInput,
      toast,
    ]
  );

  const handleOpenEditDialog = useCallback(
    (table: RestaurantTable) => {
      setEditingTable(table);
      setIsEditDialogOpen(true);
      setUpdatingTableId(null);
      setEditFormError(null);

      const originalNumber =
        table.tableNumber !== undefined && table.tableNumber !== null ? String(table.tableNumber) : '';
      setEditOriginalTableNumber(originalNumber);
      setEditLastManualTableNumber(originalNumber);
      setEditTableNumberInput(isPickupLocation(table.location) ? 'N/A' : originalNumber);

      const slug = table.qrCodeSlug ?? '';
      setEditCustomSlugInput(slug);
      setEditOriginalSlug(slug);

      const locationNameRaw = table.location?.trim() ?? '';
      setEditLocationInitialised(false);

      if (!locationNameRaw) {
        setEditSelectedLocationValue('__none');
        setEditSelectedLocationName('');
        setEditLocationInitialised(true);
        return;
      }

      const presetMatch = PRESET_LOCATION_NAMES.find(
        (name) => name.toLowerCase() === locationNameRaw.toLowerCase()
      );
      if (presetMatch) {
        setEditSelectedLocationValue(`${PRESET_LOCATION_VALUE_PREFIX}${presetMatch}`);
        setEditSelectedLocationName(presetMatch);
        if (presetMatch.toLowerCase() === 'pick up') {
          setEditTableNumberInput('N/A');
        }
        return;
      }

      const matchingLocation = availableLocations.find(
        (location) => location.name.trim().toLowerCase() === locationNameRaw.toLowerCase()
      );

      if (matchingLocation) {
        setEditSelectedLocationValue(matchingLocation.id);
        setEditSelectedLocationName(matchingLocation.name);
        setEditLocationInitialised(true);
        if (isPickupLocation(matchingLocation.name)) {
          setEditTableNumberInput('N/A');
        }
        return;
      }

      setEditSelectedLocationValue('__none');
      setEditSelectedLocationName(locationNameRaw);
    },
    [availableLocations]
  );

  const handleRequestDelete = useCallback((table: RestaurantTable) => {
    setTablePendingDelete(table);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!tablePendingDelete) {
      return;
    }

    setDeletingTableId(tablePendingDelete.id);

    try {
      if (!activeSubdomain) {
        throw new Error('missing tenant slug');
      }

      await deleteTable(activeSubdomain, tablePendingDelete.id);

      setGeneratedAssets((previous) => {
        if (!previous[tablePendingDelete.id]) {
          return previous;
        }
        const next = { ...previous };
        delete next[tablePendingDelete.id];
        return next;
      });

      const label = getTableDisplayMetadata(tablePendingDelete).displayLabel;

      setTablePendingDelete(null);

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['tables'] }),
        queryClient.invalidateQueries({ queryKey: ['tables', { restaurantId: activeRestaurantId }] }),
        queryClient.invalidateQueries({ queryKey: ['dashboard-v3', 'tables', activeSubdomain] }),
      ]);

      toast({
        title: t('dashboard:qrCodes.delete.successTitle', { defaultValue: 'Table deleted' }),
        description: t('dashboard:qrCodes.delete.successDescription', {
          defaultValue: 'Removed {{table}} from your QR directory.',
          table: label,
        }),
      });
    } catch (error) {
      console.error('[QRCodesPage] failed to delete table', error);
      const message = toErrorMessage(
        error,
        t('dashboard:qrCodes.delete.errorDescription', { defaultValue: 'Please try again.' })
      );
      toast({
        title: t('dashboard:qrCodes.delete.errorTitle', { defaultValue: 'Unable to delete table' }),
        description: message,
        variant: 'destructive',
      });
    } finally {
      setDeletingTableId(null);
    }
  }, [activeRestaurantId, activeSubdomain, queryClient, t, tablePendingDelete, toast]);

  const handleSubmitEditTable = useCallback(
    async (event?: FormEvent<HTMLFormElement>) => {
      if (event) {
        event.preventDefault();
      }

      if (!editingTable) {
        setEditFormError('Select a table to update.');
        return;
      }

      if (!activeSubdomain) {
        toast({
          title: 'Select a restaurant',
          description: 'Choose a restaurant before editing tables.',
          variant: 'destructive',
        });
        return;
      }

      setEditFormError(null);

      const updates: UpdateTableOptions = {};
      let hasChange = false;

      if (!editIsPickUpSelected) {
        const trimmed = editTableNumberInput.trim();
        if (!trimmed) {
          setEditFormError('Enter a table number to continue.');
          return;
        }
        const parsedNumber = Number.parseInt(trimmed, 10);
        if (!Number.isFinite(parsedNumber) || parsedNumber <= 0) {
          setEditFormError('Table number must be a positive number.');
          return;
        }
        const originalNumber =
          editingTable.tableNumber !== undefined && editingTable.tableNumber !== null
            ? Number.parseInt(String(editingTable.tableNumber), 10)
            : undefined;
        if (!Number.isFinite(originalNumber ?? Number.NaN) || parsedNumber !== originalNumber) {
          updates.tableNumber = parsedNumber;
          hasChange = true;
        }
      }

      if (editSelectedLocationValue === '__none') {
        if (editingTable.location) {
          updates.locationId = null;
          hasChange = true;
        }
      } else {
        const locationRecord = await getLocationRecordForSelection(
          editSelectedLocationValue,
          editSelectedLocationName
        );
        if (!locationRecord) {
          setEditFormError('Unable to resolve selected location.');
          return;
        }

        const originalLocationName = (editingTable.location ?? '').trim().toLowerCase();
        if (locationRecord.name.trim().toLowerCase() !== originalLocationName) {
          updates.locationId = locationRecord.id;
          hasChange = true;
        }
      }

      const trimmedSlug = editCustomSlugInput.trim();
      const originalSlug = editOriginalSlug.trim();
      if (trimmedSlug && trimmedSlug !== originalSlug) {
        updates.qrCodeSlug = trimmedSlug;
        hasChange = true;
      }

      if (!hasChange) {
        setEditFormError('No changes to save.');
        return;
      }

      try {
        setUpdatingTableId(editingTable.id);
        await updateTable(activeSubdomain, editingTable.id, updates);
        await queryClient.invalidateQueries({ queryKey: ['tables'] });
        await queryClient.invalidateQueries({ queryKey: ['tables', { restaurantId: activeRestaurantId }] });
        toast({ title: 'Table updated', description: 'Changes saved successfully.' });
        setIsEditDialogOpen(false);
      } catch (error) {
        console.error('[QRCodesPage] failed to update table', error);
        const message = toErrorMessage(error, 'Please try again.');
        setEditFormError(message);
        toast({ title: 'Unable to update table', description: message, variant: 'destructive' });
      } finally {
        setUpdatingTableId(null);
      }
    },
    [
      activeRestaurantId,
      activeSubdomain,
      editCustomSlugInput,
      editIsPickUpSelected,
      editOriginalSlug,
      editSelectedLocationName,
      editSelectedLocationValue,
      editTableNumberInput,
      editingTable,
      getLocationRecordForSelection,
      queryClient,
      toast,
    ]
  );

  const handleMarkReady = useCallback(
    async (table: RestaurantTable) => {
      if (!activeSubdomain) {
        toast({
          title: 'Select a restaurant',
          description: 'Choose a restaurant before updating tables.',
          variant: 'destructive',
        });
        return;
      }

      const { displayLabel } = getTableDisplayMetadata(table);
      const pendingCashCents = Math.max(0, table.pendingCashTotalCents ?? 0);
      if (table.hasPendingCash || pendingCashCents > 0) {
        const pendingAmountLabel =
          pendingCashCents > 0 ? formatCurrency(pendingCashCents, currencyCode, { isMinorUnit: true }) : null;
        toast({
          title: t('dashboard:qrCodes.pendingPayments.toastTitle', {
            defaultValue: 'Outstanding balance',
          }),
          description: pendingAmountLabel
            ? t('dashboard:qrCodes.pendingPayments.toastDescription', {
                defaultValue: 'Collect {{amount}} before marking table {{table}} ready.',
                amount: pendingAmountLabel,
                table: displayLabel,
              })
            : t('dashboard:qrCodes.pendingPayments.toastDescriptionUnknown', {
                defaultValue: 'Collect outstanding payments before marking table {{table}} ready.',
                table: displayLabel,
              }),
          variant: 'destructive',
        });
        return;
      }

      try {
        setReleasingTableId(table.id);
        const response = await releaseTable(activeSubdomain, table.id);

        const applyReadyState = (dataset: FetchTablesResponse | undefined): FetchTablesResponse | undefined => {
          if (!dataset) return dataset;
          const tables = dataset.tables.map((existing) =>
            existing.id === table.id
              ? { ...existing, status: 'OPEN' as const, lastOrderAt: null }
              : existing
          );
          return { ...dataset, tables };
        };

        queryClient.setQueryData<FetchTablesResponse | undefined>(
          ['tables'],
          (existing) => applyReadyState(existing)
        );
        queryClient.setQueryData<FetchTablesResponse | undefined>(
          ['tables', { restaurantId: activeRestaurantId }],
          (existing) => applyReadyState(existing)
        );
        if (activeSubdomain) {
          queryClient.setQueryData<FetchTablesResponse | undefined>(
            ['tables', { restaurantId: activeRestaurantId, subdomain: activeSubdomain }],
            (existing) => applyReadyState(existing)
          );
          queryClient.setQueryData<FetchTablesResponse | undefined>(
            ['dashboard-v3', 'tables', activeSubdomain],
            (existing) => applyReadyState(existing)
          );
          queryClient.setQueriesData<unknown>(
            { queryKey: ['dashboard-v3', 'orders'] },
            (existing) => {
              if (!Array.isArray(existing)) {
                return existing;
              }
              return existing.map((order) => {
                if (isDashboardOrderLike(order) && order.table_id === table.id) {
                  const statusValue =
                    typeof order.status === 'string'
                      ? order.status.toUpperCase()
                      : undefined;
                  if (
                    !statusValue ||
                    [
                      'PENDING_PAYMENT',
                      'NEW',
                      'PAID',
                      'ACCEPTED',
                      'IN_PROGRESS',
                      'PREPARING',
                      'READY',
                      'OPEN',
                      'SERVED',
                      'PICKED_UP',
                    ].includes(statusValue)
                  ) {
                    return { ...order, status: 'COMPLETED', table_id: null };
                  }
                  return { ...order, status: 'READY' };
                }
                return order;
              });
            }
          );
          queryClient.setQueriesData<TableActivitySummaryResponse | undefined>(
            { queryKey: ['dashboard-v3', 'table-activity'] },
            (existing) => {
              if (!existing) {
                return existing;
              }
              const activeTableIds = (existing.activeTableIds ?? []).filter((id) => id !== table.id);
              const reservedTableIds = (existing.reservedTableIds ?? []).filter((id) => id !== table.id);
              const totals = existing.totals ?? { total: 0, active: 0, reserved: 0, available: 0 };
              const nextTotals = {
                ...totals,
                active: Math.max(0, totals.active - (totals.active > 0 && existing.activeTableIds?.includes(table.id) ? 1 : 0)),
                reserved: Math.max(
                  0,
                  totals.reserved - (totals.reserved > 0 && existing.reservedTableIds?.includes(table.id) ? 1 : 0)
                ),
              };
              const available = Math.max(0, nextTotals.total - nextTotals.active - nextTotals.reserved);
              nextTotals.available = available;
              return {
                ...existing,
                activeTableIds,
                reservedTableIds,
                totals: nextTotals,
              };
            }
          );
        }

        const invalidations: Array<Promise<void>> = [
          queryClient.invalidateQueries({ queryKey: ['tables'] }),
          queryClient.invalidateQueries({ queryKey: ['tables', { restaurantId: activeRestaurantId }] }),
          queryClient.invalidateQueries({ queryKey: ['qr-metrics'] }),
          queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
          queryClient.invalidateQueries({ queryKey: ['orders'] }),
          queryClient.invalidateQueries({ queryKey: ['payments'] }),
          queryClient.invalidateQueries({ queryKey: ['dashboard-v3'] }),
        ];
        if (activeSubdomain) {
          invalidations.push(
            queryClient.invalidateQueries({ queryKey: ['tables', { restaurantId: activeRestaurantId, subdomain: activeSubdomain }] }),
            queryClient.invalidateQueries({ queryKey: ['dashboard-v3', 'tables', activeSubdomain] }),
            queryClient.invalidateQueries({ queryKey: ['dashboard-v3', 'orders'] }),
            queryClient.invalidateQueries({ queryKey: ['dashboard-v3', 'payments'] }),
            queryClient.invalidateQueries({ queryKey: ['dashboard-v3', 'qr-metrics'] }),
            queryClient.invalidateQueries({ queryKey: ['dashboard-v3', 'table-activity'] })
          );
        }
        await Promise.all(invalidations);

        const orderSummary =
          response.completedOrders > 0
            ? `${response.completedOrders} open ${response.completedOrders === 1 ? 'order' : 'orders'} completed.`
            : 'No active orders were associated.';

        toast({
          title: `Table ${displayLabel} ready`,
          description: `${orderSummary} Ready for the next guests.`,
        });
      } catch (error) {
        console.error('[QRCodesPage] failed to mark table ready', error);
        const message = toErrorMessage(error, 'Please try again.');
        toast({ title: 'Unable to mark ready', description: message, variant: 'destructive' });
      } finally {
        setReleasingTableId(null);
      }
    },
    [activeRestaurantId, activeSubdomain, currencyCode, formatCurrency, queryClient, t, toast]
  );

  const summary = useMemo(() => {
    const total = tables.length;
    const occupied = tables.filter((t) => t.status === 'OCCUPIED').length;
    const reserved = tables.filter((t) => t.status === 'RESERVED').length;
    const availableRaw = total - occupied - reserved;
    const available = Math.max(0, availableRaw);
    return { total, occupied, reserved, available };
  }, [tables]);

  const qrMetricsSummary = useMemo(() => {
    type TableStats = {
      scansCount: number;
      ordersCount: number;
      revenueCents: number;
      lastActivityAt: string | null;
    };

    const emptyTotals = {
      totalTables: 0,
      scansCount: 0,
      ordersCount: 0,
      revenueCents: 0,
      lastActivityAt: null as string | null,
    };

    const statsByTable = new Map<string, TableStats>();
    const metrics = qrMetricsQuery.data;

    if (!metrics) {
      return {
        totals: { ...emptyTotals },
        period: null as QrMetricsResponse['period'] | null,
        statsByTable,
      };
    }

    for (const entry of metrics.tables) {
      statsByTable.set(entry.tableId, {
        scansCount: entry.scansCount,
        ordersCount: entry.ordersCount,
        revenueCents: entry.revenueCents,
        lastActivityAt: entry.lastActivityAt ?? null,
      });
    }

    return {
      totals: metrics.totals,
      period: metrics.period,
      statsByTable,
    };
  }, [qrMetricsQuery.data]);

  const metrics = useMemo(() => {
    const total = summary.total;
    const cacheStatus = tablesQuery.data?.cacheStatus ?? 'network';
    const lastSynced = tablesQuery.data?.cachedAt
      ? formatDistanceToNow(new Date(tablesQuery.data.cachedAt), { addSuffix: true })
      : null;

    const activeToday = summary.occupied;
    const activePct = total > 0 ? Math.round((activeToday / total) * 100) : 0;

    const totals = qrMetricsSummary.totals;
    const periodLabel = qrMetricsSummary.period?.label ?? 'Today';
    const totalScans = totals.scansCount;
    const totalOrders = totals.ordersCount;
    const lastScanLabel = totals.lastActivityAt
      ? formatDistanceToNow(new Date(totals.lastActivityAt), { addSuffix: true })
      : null;

    const revenueLabel = formatCurrency(totals.revenueCents, currencyCode, { isMinorUnit: true });

    return [
      {
        label: 'Total QR Codes',
        value: formatNumber(total),
        helper: lastSynced ? `Synced ${lastSynced}` : 'Live sync',
        icon: QrCode,
        gradient: 'from-emerald-500/10 via-emerald-500/5 to-emerald-600/5',
        gradientDark: 'dark:from-emerald-500/20 dark:via-emerald-500/10 dark:to-emerald-600/10',
        iconBg: 'bg-emerald-500/10 border border-emerald-500/20',
        iconBgDark: 'dark:bg-emerald-500/20 dark:border-emerald-500/30',
        iconColor: 'text-emerald-600 dark:text-emerald-400',
        trend: cacheStatus === 'cache' ? 'Offline snapshot' : 'Live connection',
        trendColor:
          cacheStatus === 'cache'
            ? 'text-amber-600 dark:text-amber-400'
            : 'text-emerald-600 dark:text-emerald-400',
      },
      {
        label: 'Active Today',
        value: formatNumber(activeToday),
        helper: total ? `${activePct}% of tables` : 'Awaiting first table',
        icon: CheckCircle2,
        gradient: 'from-emerald-500/10 via-emerald-500/5 to-emerald-600/5',
        gradientDark: 'dark:from-emerald-500/20 dark:via-emerald-500/10 dark:to-emerald-600/10',
        iconBg: 'bg-emerald-500/10 border border-emerald-500/20',
        iconBgDark: 'dark:bg-emerald-500/20 dark:border-emerald-500/30',
        iconColor: 'text-emerald-600 dark:text-emerald-400',
        trend: activeToday > 0 ? 'Guests active now' : 'No active tables',
        trendColor:
          activeToday > 0
            ? 'text-emerald-600 dark:text-emerald-400'
            : 'text-muted-foreground',
      },
      {
        label: 'Total Scans',
        value: formatNumber(totalScans),
        helper: lastScanLabel ? `Latest ${lastScanLabel}` : 'No scans recorded this period',
        icon: BarChart3,
        gradient: 'from-sky-500/10 via-sky-500/5 to-sky-600/5',
        gradientDark: 'dark:from-sky-500/20 dark:via-sky-500/10 dark:to-sky-600/10',
        iconBg: 'bg-sky-500/10 border border-sky-500/20',
        iconBgDark: 'dark:bg-sky-500/20 dark:border-sky-500/30',
        iconColor: 'text-sky-600 dark:text-sky-400',
        trend: totalScans > 0 ? `Engagement ${periodLabel.toLowerCase()}` : 'No QR scans yet',
        trendColor:
          totalScans > 0
            ? 'text-emerald-600 dark:text-emerald-400'
            : 'text-muted-foreground',
      },
      {
        label: 'QR Revenue',
        value: revenueLabel,
        helper:
          totalOrders > 0
            ? `${formatNumber(totalOrders)} orders ${periodLabel !== 'Today' ? `(${periodLabel})` : 'this period'}`
            : 'Awaiting first order',
        icon: DollarSign,
        gradient: 'from-orange-500/10 via-orange-500/5 to-orange-600/5',
        gradientDark: 'dark:from-orange-500/20 dark:via-orange-500/10 dark:to-orange-600/10',
        iconBg: 'bg-orange-500/10 border border-orange-500/20',
        iconBgDark: 'dark:bg-orange-500/20 dark:border-orange-500/30',
        iconColor: 'text-orange-600 dark:text-orange-400',
        trend: totals.revenueCents > 0 ? 'Revenue via QR this period' : 'No QR revenue yet',
        trendColor:
          totals.revenueCents > 0
            ? 'text-emerald-600 dark:text-emerald-400'
            : 'text-muted-foreground',
      },
    ];
  }, [
    qrMetricsSummary,
    currencyCode,
    formatCurrency,
    formatNumber,
    summary,
    tablesQuery.data?.cacheStatus,
    tablesQuery.data?.cachedAt,
  ]);

  const filteredTables = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return tables.filter((table) => {
      const metadata = getTableDisplayMetadata(table);
      const rawId = table.tableNumber ?? table.qrCodeSlug ?? '';
      const tableId = String(rawId).toLowerCase();
      const displayId = metadata.displayLabel.toLowerCase();
      const location = (table.location ?? '').toLowerCase();
      const matchesTerm =
        term.length === 0 || tableId.includes(term) || displayId.includes(term) || location.includes(term);

      let matchesStatus = true;
      if (statusFilter === 'available') {
        matchesStatus = !table.status || table.status === 'AVAILABLE';
      } else if (statusFilter === 'reserved') {
        matchesStatus = table.status === 'RESERVED';
      } else if (statusFilter === 'occupied') {
        matchesStatus = table.status === 'OCCUPIED';
      }

      return matchesTerm && matchesStatus;
    });
  }, [tables, searchTerm, statusFilter]);

  const pendingDeleteLabel = useMemo(() => {
    if (!tablePendingDelete) {
      return '';
    }
    return getTableDisplayMetadata(tablePendingDelete).displayLabel;
  }, [tablePendingDelete]);

  const ensureAssets = useCallback(
    async (table: RestaurantTable): Promise<GeneratedAssets> => {
      const cached = generatedAssets[table.id];
      if (cached?.pngDataUrl) return cached;

      const baseDeepLink = computeDeepLink(table, { demo: false });

      if (table.qrPngDataUrl) {
        const assets: GeneratedAssets = { pngDataUrl: table.qrPngDataUrl, deepLink: baseDeepLink };
        setGeneratedAssets((prev) => ({ ...prev, [table.id]: assets }));
        return assets;
      }

      if (!activeSubdomain) throw new Error('missing tenant slug');
      const { table: refreshedTable, assets: regenAssets } = await regenTableQr(activeSubdomain, table.id);

      const generated: GeneratedAssets = {
        pngDataUrl: regenAssets.pngDataUrl || refreshedTable.qrPngDataUrl || table.qrPngDataUrl,
        deepLink: regenAssets.deepLink || refreshedTable.deepLink || baseDeepLink,
        svg: regenAssets.svg,
      };

      setGeneratedAssets((prev) => ({ ...prev, [table.id]: generated }));
      queryClient.invalidateQueries({ queryKey: ['tables'] });
      queryClient.invalidateQueries({ queryKey: ['tables', { restaurantId: activeRestaurantId }] });

      if (import.meta.env?.DEV) console.debug('[QRCodesPage] QR assets regenerated for table', table.id);
      return generated;
    },
    [activeRestaurantId, activeSubdomain, computeDeepLink, generatedAssets, queryClient]
  );

  const handleCopyLink = useCallback(
    async (table: RestaurantTable) => {
      const url = computeDeepLink(table, { demo: false });
      if (!url) {
        toast({
          title: 'Select a restaurant',
          description: 'Choose a restaurant to copy deep links.',
          variant: 'destructive',
        });
        return;
      }
      try {
        await navigator.clipboard.writeText(url);
        setCopiedTableId(table.id);
        toast({ title: 'Link copied', description: 'Public ordering link copied to clipboard.' });
      } catch (error) {
        console.error('Failed to copy link', error);
        const message = toErrorMessage(error, 'Copy the link manually.');
        toast({ title: 'Unable to copy', description: message, variant: 'destructive' });
      }
    },
    [computeDeepLink, toast]
  );

  const handleOpenOnPhone = useCallback(
    (table: RestaurantTable) => {
      const phoneUrl = computeDeepLink(table, { forceLocal: true });
      if (!phoneUrl) {
        toast({
          title: 'Phone link unavailable',
          description: 'Open the dev server directly or set VITE_PUBLIC_ORIGIN.',
          variant: 'destructive',
        });
        return;
      }
      toast({ title: 'Phone link ready', description: phoneUrl });
      window.open(phoneUrl, '_blank', 'noopener');
    },
    [computeDeepLink, toast]
  );

  const handleGenerate = useCallback(
    async (table: RestaurantTable) => {
      try {
        setGeneratingTableId(table.id);
        const assets = await ensureAssets(table);
        toast({ title: 'QR ready', description: assets.deepLink || computeDeepLink(table, { demo: false }) });
      } catch (error) {
        console.error('Failed to generate QR', error);
        const message = toErrorMessage(error, 'Please try again.');
        toast({ title: 'Unable to generate QR', description: message, variant: 'destructive' });
      } finally {
        setGeneratingTableId(null);
      }
    },
    [computeDeepLink, ensureAssets, toast]
  );

  const handleDownload = useCallback(
    async (table: RestaurantTable) => {
      try {
        const assets = await ensureAssets(table);
        if (!assets?.pngDataUrl) throw new Error('QR image unavailable');
        const link = document.createElement('a');
        link.href = assets.pngDataUrl;
        const { fileStem } = getTableDisplayMetadata(table);
        link.download = `${fileStem}-qr.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error('Failed to download QR', error);
        const message = toErrorMessage(error, 'Please try again.');
        toast({ title: 'Unable to download', description: message, variant: 'destructive' });
      }
    },
    [ensureAssets, toast]
  );

  const handleDownloadAll = useCallback(async () => {
    if (!tables.length) return;
    setIsBulkGenerating(true);
    try {
      for (const table of tables) {
        const assets = await ensureAssets(table);
        if (!assets?.pngDataUrl) continue;
        const link = document.createElement('a');
        link.href = assets.pngDataUrl;
        const { fileStem } = getTableDisplayMetadata(table);
        link.download = `${fileStem}-qr.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        await new Promise((resolve) => window.setTimeout(resolve, 120));
      }
      toast({ title: 'Downloads started', description: 'Check your downloads folder for QR images.' });
    } catch (error) {
      console.error('Failed to download all QRs', error);
      const message = toErrorMessage(error, 'Please try again.');
      toast({ title: 'Unable to download all', description: message, variant: 'destructive' });
    } finally {
      setIsBulkGenerating(false);
    }
  }, [ensureAssets, tables, toast]);

  const handlePrintAll = useCallback(async () => {
    if (!tables.length) return;
    setIsBulkGenerating(true);
    try {
      const assetsList: Array<{ table: RestaurantTable; assets: GeneratedAssets }> = [];
      for (const table of tables) {
        const assets = await ensureAssets(table);
        assetsList.push({ table, assets });
      }
      const printWindow = window.open('', '_blank');
      if (!printWindow) throw new Error('Pop-up blocked. Allow pop-ups to print QR codes.');

      const content = assetsList
        .map(({ table, assets }) => {
          const deepLink = assets.deepLink || computeDeepLink(table, { demo: false });
          const { displayLabel } = getTableDisplayMetadata(table);
          return `
            <div style="page-break-inside: avoid; margin-bottom: 40px; text-align: center;">
              <h2 style="margin-bottom: 20px;">Table ${displayLabel}</h2>
              <img
                src="${assets.pngDataUrl}"
                alt="QR Code for ${displayLabel}"
                style="border: 2px solid #ccc; padding: 10px;"
              />
              <p style="margin-top: 15px; font-size: 14px; color: #666;">
                Scan to order from ${restaurantName}
              </p>
              <p style="margin-top: 5px; font-size: 12px; color: #999; word-break: break-all;">
                ${deepLink}
              </p>
            </div>
          `;
        })
        .join('');

      printWindow!.document.write(`
        <html>
          <head>
            <title>QR Codes - ${restaurantName}</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; }
              @media print {
                body { margin: 0; }
                .no-print { display: none; }
              }
            </style>
          </head>
          <body>
            <h1 style="text-align: center; margin-bottom: 40px;">QR Codes for ${restaurantName}</h1>
            ${content}
          </body>
        </html>
      `);
      printWindow!.document.close();
      printWindow!.print();
    } catch (error) {
      console.error('Failed to print QRs', error);
      const message = toErrorMessage(error, 'Please try again.');
      toast({ title: 'Unable to print', description: message, variant: 'destructive' });
    } finally {
      setIsBulkGenerating(false);
    }
  }, [computeDeepLink, ensureAssets, tables, restaurantName, toast]);

  const handleRefresh = useCallback(() => {
    if (!activeRestaurantId) return;
    queryClient.invalidateQueries({ queryKey: ['tables'] });
    queryClient.invalidateQueries({ queryKey: ['tables', { restaurantId: activeRestaurantId }] });
    if (import.meta.env?.DEV) console.debug('[QRCodesPage] manual refresh requested');
  }, [activeRestaurantId, queryClient]);

  if (!isRestaurantSelected) {
    return (
      <div className="min-h-screen bg-muted/10 px-4 py-10" data-testid="qr-codes-page">
        <div className="mx-auto max-w-3xl">
          <Card className="border border-dashed border-emerald-300/50 bg-background shadow-sm">
            <CardHeader className="space-y-4 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl border border-emerald-500/30 bg-emerald-500/10">
                <QrCode className="h-6 w-6 text-emerald-600" />
              </div>
              <CardTitle className="text-2xl font-semibold text-foreground">
                Select a restaurant to manage QR codes
              </CardTitle>
              <CardDescription className="text-sm text-muted-foreground">
                Use the restaurant switcher in the header to choose a location. Once selected, this page loads the live QR directory.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col items-center gap-3">
              <Button asChild className="rounded-xl px-6">
                <Link to={backHref}>Go to Dashboard</Link>
              </Button>
              <p className="text-xs text-muted-foreground">
                Tip: you can switch restaurants from any page without losing context.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (tablesQuery.isLoading) {
    return (
      <div className="min-h-screen bg-muted/10 px-4 py-10" data-testid="qr-codes-page">
        <div className="mx-auto max-w-7xl space-y-8">
          <Card className="border border-border bg-card shadow-sm">
            <CardContent className="flex flex-col items-center gap-4 py-16">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-emerald-500/20 bg-emerald-500/10">
                <QrCode className="h-7 w-7 animate-pulse text-emerald-600" />
              </div>
              <h2 className="text-xl font-semibold text-foreground">Loading QR codes...</h2>
              <p className="text-sm text-muted-foreground">
                We're retrieving your tables and existing QR assets.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (tablesQuery.isError) {
    return (
      <div className="min-h-screen bg-muted/10 px-4 py-10" data-testid="qr-codes-page">
        <div className="mx-auto max-w-3xl">
          <Card className="border border-rose-200/60 bg-card shadow-sm">
            <CardHeader className="space-y-3 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl border border-rose-500/30 bg-rose-500/10">
                <AlertCircle className="h-6 w-6 text-rose-600" />
              </div>
              <CardTitle className="text-2xl font-semibold text-foreground">
                Unable to load QR codes
              </CardTitle>
              <CardDescription className="text-sm text-muted-foreground">{errorMessage}</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col items-center gap-3">
              <Button
                onClick={handleRefresh}
                disabled={tablesQuery.isFetching}
                className="rounded-xl bg-emerald-600 px-6 text-white hover:bg-emerald-700"
              >
                Retry
              </Button>
              <p className="text-xs text-muted-foreground">
                Ensure a restaurant is selected and you have network access.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const cacheStatus = tablesQuery.data?.cacheStatus ?? 'network';
  const lastSyncedLabel = tablesQuery.data?.cachedAt
    ? formatDistanceToNow(new Date(tablesQuery.data.cachedAt), { addSuffix: true })
    : null;
  const editingTableLabel = editingTable ? getTableDisplayMetadata(editingTable).displayLabel : null;
  const isUpdatingTable = Boolean(updatingTableId);

  return (
    <>
      <Dialog
        open={isCreateDialogOpen}
        onOpenChange={(open) => {
          if (creatingTable && !open) {
            setIsCreateDialogOpen(false);
            return;
          }
          if (!creatingTable || open) {
            setIsCreateDialogOpen(open);
          }
        }}
      >
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Create a new table</DialogTitle>
            <DialogDescription>
              Assign a table number and where it lives. We'll generate the QR code instantly so you can print or share it.
            </DialogDescription>
          </DialogHeader>
          <form className="space-y-5" onSubmit={handleSubmitNewTable}>
            <div className="space-y-2">
              <Label htmlFor="table-number">Table number</Label>
              <Input
                id="table-number"
                inputMode="numeric"
                placeholder={isPickUpSelected ? 'N/A' : 'e.g. 12'}
                value={tableNumberInput}
                onChange={(event) => {
                  if (isPickUpSelected) {
                    setTableNumberInput('N/A');
                    return;
                  }
                  const value = event.target.value;
                  setTableNumberInput(value);
                  setLastManualTableNumber(value);
                }}
                disabled={isPickUpSelected}
                autoFocus
              />
              <p className="text-xs text-muted-foreground">
                This appears across dashboards and the QR directory. Pick Up locations automatically use N/A.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="table-location">Location (optional)</Label>
              <Select
                value={selectedLocationValue}
                onValueChange={handleLocationChange}
                disabled={locationsQuery.isLoading}
              >
                <SelectTrigger id="table-location">
                  <SelectValue placeholder={locationsQuery.isLoading ? 'Loading locations...' : 'Assign an area'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">No location</SelectItem>
                  {presetLocationOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                  {additionalLocations.map((location) => (
                    <SelectItem key={location.id} value={location.id}>
                      {location.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Use locations to separate dining room, patio, bar, or private spaces. Pick Up locations automatically
                label the table number as N/A.
              </p>
              {locationsQuery.isError && (
                <p className="text-xs text-destructive">
                  We could not load locations right now. You can still create the table and assign an area later.
                </p>
              )}
              {availableLocations.length === 0 && !locationsQuery.isLoading && (
                <p className="text-xs text-muted-foreground">
                  Manage locations from Settings &gt; Layout to add more areas.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="table-slug">Custom QR link (optional)</Label>
              <Input
                id="table-slug"
                placeholder="e.g. patio-12"
                value={customSlugInput}
                onChange={(event) => setCustomSlugInput(event.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Leave blank to auto-generate a unique link. Only letters, numbers, and dashes are recommended.
              </p>
            </div>

            {formError && <p className="text-sm text-destructive">{formError}</p>}

            <DialogFooter className="gap-2 sm:gap-3">
              <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)} disabled={creatingTable}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-emerald-600 text-white hover:bg-emerald-700"
                disabled={creatingTable}
              >
                {creatingTable ? 'Creating...' : 'Create table'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog
        open={isBulkCreateDialogOpen}
        onOpenChange={(open) => {
          if (isBulkCreatingTables && !open) {
            setIsBulkCreateDialogOpen(false);
            return;
          }
          if (!isBulkCreatingTables || open) {
            setIsBulkCreateDialogOpen(open);
          }
        }}
      >
        <DialogContent className="sm:max-w-[640px]">
          <DialogHeader>
            <DialogTitle>
              {t('dashboard:qrCodes.bulkDialog.title', { defaultValue: 'Add multiple tables' })}
            </DialogTitle>
            <DialogDescription>
              {t('dashboard:qrCodes.bulkDialog.description', {
                defaultValue: 'Create batches of tables by location and generate their QR codes in one step.',
              })}
            </DialogDescription>
          </DialogHeader>
          <form className="space-y-6" onSubmit={handleSubmitBulkTables}>
            <div className="space-y-4">
              {bulkEntries.map((entry, index) => {
                const isPickup = isPickupLocation(entry.locationName);
                return (
                  <div key={entry.id} className="space-y-4 rounded-xl border border-border bg-card/40 p-4">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-muted-foreground">
                        {t('dashboard:qrCodes.bulkDialog.batchLabel', {
                          defaultValue: 'Batch {{index}}',
                          index: index + 1,
                        })}
                      </p>
                      {bulkEntries.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveBulkEntry(entry.id)}
                          disabled={isBulkCreatingTables}
                          aria-label={t('dashboard:qrCodes.bulkDialog.removeBatch', {
                            defaultValue: 'Remove batch {{index}}',
                            index: index + 1,
                          })}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    <div className="grid gap-3 sm:grid-cols-3">
                      <div className="space-y-2">
                        <Label htmlFor={`bulk-location-${entry.id}`}>
                          {t('dashboard:qrCodes.bulkDialog.locationLabel', { defaultValue: 'Location (optional)' })}
                        </Label>
                        <Select
                          value={entry.locationValue}
                          onValueChange={(value) => handleBulkLocationChange(entry.id, value)}
                          disabled={locationsQuery.isLoading || isBulkCreatingTables}
                        >
                          <SelectTrigger id={`bulk-location-${entry.id}`}>
                            <SelectValue
                              placeholder={
                                locationsQuery.isLoading
                                  ? t('dashboard:qrCodes.bulkDialog.locationLoading', {
                                      defaultValue: 'Loading locations...',
                                    })
                                  : t('dashboard:qrCodes.bulkDialog.locationPlaceholder', {
                                      defaultValue: 'Assign an area',
                                    })
                              }
                            />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none">
                              {t('dashboard:qrCodes.bulkDialog.locationNone', { defaultValue: 'No location' })}
                            </SelectItem>
                            {presetLocationOptions.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                            {additionalLocations.map((location) => (
                              <SelectItem key={location.id} value={location.id}>
                                {location.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          {t('dashboard:qrCodes.bulkDialog.locationHelper', {
                            defaultValue: 'Use locations to separate dining room, patio, bar, or other areas.',
                          })}
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor={`bulk-start-${entry.id}`}>
                          {t('dashboard:qrCodes.bulkDialog.startLabel', { defaultValue: 'Starting table number' })}
                        </Label>
                        <Input
                          id={`bulk-start-${entry.id}`}
                          inputMode="numeric"
                          placeholder={
                            isPickup
                              ? t('dashboard:qrCodes.bulkDialog.pickupPlaceholder', { defaultValue: 'N/A for pick up' })
                              : t('dashboard:qrCodes.bulkDialog.startPlaceholder', { defaultValue: 'e.g. 21' })
                          }
                          value={isPickup ? 'N/A' : entry.startNumber}
                          onChange={(event) => handleBulkFieldChange(entry.id, 'startNumber', event.target.value)}
                          disabled={isPickup || isBulkCreatingTables}
                        />
                        <p className="text-xs text-muted-foreground">
                          {t('dashboard:qrCodes.bulkDialog.startHelper', {
                            defaultValue: 'We will increment from this number for the batch.',
                          })}
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor={`bulk-count-${entry.id}`}>
                          {t('dashboard:qrCodes.bulkDialog.countLabel', { defaultValue: 'Number of tables' })}
                        </Label>
                        <Input
                          id={`bulk-count-${entry.id}`}
                          type="number"
                          inputMode="numeric"
                          min={1}
                          max={50}
                          value={entry.count}
                          onChange={(event) => handleBulkFieldChange(entry.id, 'count', event.target.value)}
                          disabled={isBulkCreatingTables}
                        />
                        <p className="text-xs text-muted-foreground">
                          {t('dashboard:qrCodes.bulkDialog.countHelper', {
                            defaultValue: 'Add at least one table. Increase to create sequential tables automatically.',
                          })}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex items-center justify-between">
              <Button
                type="button"
                variant="outline"
                onClick={handleAddBulkEntry}
                disabled={isBulkCreatingTables}
              >
                <Plus className="mr-2 h-4 w-4" />
                {t('dashboard:qrCodes.bulkDialog.addBatch', { defaultValue: 'Add another batch' })}
              </Button>
              <div className="text-xs text-muted-foreground">
                {t('dashboard:qrCodes.bulkDialog.batchSummary', {
                  defaultValue: 'Batches run sequentially to prevent duplicate numbers.',
                })}
              </div>
            </div>

            {bulkFormError && <p className="text-sm text-destructive">{bulkFormError}</p>}

            <DialogFooter className="gap-2 sm:gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsBulkCreateDialogOpen(false)}
                disabled={isBulkCreatingTables}
              >
                {t('dashboard:qrCodes.bulkDialog.cancel', { defaultValue: 'Cancel' })}
              </Button>
              <Button
                type="submit"
                className="bg-emerald-600 text-white hover:bg-emerald-700"
                disabled={isBulkCreatingTables}
              >
                {isBulkCreatingTables
                  ? t('dashboard:qrCodes.bulkDialog.creating', { defaultValue: 'Creating tables...' })
                  : t('dashboard:qrCodes.bulkDialog.create', { defaultValue: 'Create tables' })}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog
        open={isEditDialogOpen}
        onOpenChange={(open) => {
          if (isUpdatingTable && !open) {
            setIsEditDialogOpen(false);
            return;
          }
          if (!isUpdatingTable || open) {
            setIsEditDialogOpen(open);
          }
        }}
      >
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>
              {editingTableLabel ? `Edit table ${editingTableLabel}` : 'Edit table'}
            </DialogTitle>
            <DialogDescription>
              Update the table number, assigned location, or QR link. Changes apply instantly for guests and staff.
            </DialogDescription>
          </DialogHeader>
          <form className="space-y-5" onSubmit={handleSubmitEditTable}>
            <div className="space-y-2">
              <Label htmlFor="edit-table-number">Table number</Label>
              <Input
                id="edit-table-number"
                inputMode="numeric"
                placeholder={editIsPickUpSelected ? 'N/A' : 'e.g. 12'}
                value={editTableNumberInput}
                onChange={(event) => {
                  if (editIsPickUpSelected) {
                    setEditTableNumberInput('N/A');
                    return;
                  }
                  const value = event.target.value;
                  setEditTableNumberInput(value);
                  setEditLastManualTableNumber(value);
                }}
                disabled={editIsPickUpSelected || isUpdatingTable}
                autoFocus
              />
              <p className="text-xs text-muted-foreground">
                This updates dashboards and reports. Pick Up locations automatically display N/A.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-table-location">Location</Label>
              <Select
                value={editSelectedLocationValue}
                onValueChange={handleEditLocationChange}
                disabled={locationsQuery.isLoading || isUpdatingTable}
              >
                <SelectTrigger id="edit-table-location">
                  <SelectValue placeholder={locationsQuery.isLoading ? 'Loading locations...' : 'Assign an area'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">No location</SelectItem>
                  {presetLocationOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                  {additionalLocations.map((location) => (
                    <SelectItem key={location.id} value={location.id}>
                      {location.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Switch between dining areas, patio, or service stations. Pick Up locations display the QR as N/A.
              </p>
              {locationsQuery.isError && (
                <p className="text-xs text-destructive">We could not load locations right now.</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-table-slug">Custom QR link</Label>
              <Input
                id="edit-table-slug"
                placeholder="e.g. patio-12"
                value={editCustomSlugInput}
                onChange={(event) => setEditCustomSlugInput(event.target.value)}
                disabled={isUpdatingTable}
              />
              <p className="text-xs text-muted-foreground">
                Set a friendly slug for the QR link. Leave blank to keep the current value ({editOriginalSlug || 'auto-generated slug'}).
              </p>
            </div>

            {editFormError && <p className="text-sm text-destructive">{editFormError}</p>}

            <DialogFooter className="gap-2 sm:gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditDialogOpen(false)}
                disabled={isUpdatingTable}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-emerald-600 text-white hover:bg-emerald-700"
                disabled={isUpdatingTable}
              >
                {isUpdatingTable ? 'Saving...' : 'Save changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <AlertDialog
        open={Boolean(tablePendingDelete)}
        onOpenChange={(open) => {
          if (!open && !deletingTableId) {
            setTablePendingDelete(null);
          }
        }}
      >
        <AlertDialogContent className="sm:max-w-[400px]">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('dashboard:qrCodes.delete.title', {
                defaultValue: pendingDeleteLabel ? 'Delete {{table}}?' : 'Delete this table?',
                table: pendingDeleteLabel,
              })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('dashboard:qrCodes.delete.description', {
                defaultValue:
                  'This removes the QR code and table record. Close any active orders before deleting.',
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {pendingDeleteLabel ? (
            <div className="rounded-md border border-border bg-muted/50 px-3 py-2 text-sm font-medium text-foreground">
              {pendingDeleteLabel}
            </div>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={Boolean(deletingTableId)}>
              {t('dashboard:qrCodes.delete.cancel', { defaultValue: 'Cancel' })}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void handleConfirmDelete()}
              disabled={Boolean(deletingTableId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 focus-visible:ring-destructive/50"
            >
              {deletingTableId ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('dashboard:qrCodes.delete.processing', { defaultValue: 'Deleting...' })}
                </>
              ) : (
                t('dashboard:qrCodes.delete.confirm', { defaultValue: 'Delete table' })
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="min-h-screen bg-muted/10 px-4 py-10" data-testid="qr-codes-page">
        <div className="mx-auto flex max-w-7xl flex-col gap-6">
          <div className="flex flex-col gap-4 rounded-3xl border border-border bg-card/60 p-6 shadow-sm backdrop-blur-sm sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="flex items-center gap-3">
                <div className="rounded-xl border border-emerald-500/20 bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 p-2.5">
                <QrCode className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <h1 className="text-3xl font-semibold text-foreground">QR Code Management</h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  Manage, track, and optimize your table ordering QR codes for {restaurantName}.
                </p>
              </div>
            </div>
            <p className="mt-4 text-xs text-muted-foreground">
              {lastSyncedLabel ? `Synced ${lastSyncedLabel}.` : 'Live connection to your restaurant tables.'}
            </p>
          </div>
          <div className="flex w-full flex-wrap items-center justify-start gap-2 sm:w-auto sm:justify-end">
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="border-border bg-card hover:bg-accent"
                >
                  <SlidersHorizontal className="mr-2 h-4 w-4" />
                  {t('dashboard:qrOrdering.triggerLabel', { defaultValue: 'Manage QR ordering' })}
                </Button>
              </PopoverTrigger>
              <PopoverContent
                align="end"
                sideOffset={8}
                className="w-[360px] max-w-[90vw] overflow-hidden p-0 sm:w-[420px]"
              >
                <QrOrderingStatusCard className="border-none bg-transparent shadow-none" />
              </PopoverContent>
            </Popover>
            <Button
              variant="outline"
              className="border-border bg-card hover:bg-accent"
              onClick={handleRefresh}
              disabled={tablesQuery.isFetching}
            >
              <RefreshCcw className={`mr-2 h-4 w-4 ${tablesQuery.isFetching ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              variant="outline"
              className="border-border bg-card hover:bg-accent"
              onClick={handlePrintAll}
              disabled={isBulkGenerating || tables.length === 0}
            >
              <Printer className="mr-2 h-4 w-4" />
              Print all
            </Button>
            <Button
              variant="outline"
              className="border-border bg-card hover:bg-accent"
              onClick={handleDownloadAll}
              disabled={isBulkGenerating || tables.length === 0}
            >
              <Download className="mr-2 h-4 w-4" />
              {isBulkGenerating ? 'Preparing...' : 'Download all'}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  className="bg-emerald-600 text-white shadow-lg shadow-emerald-500/25 hover:bg-emerald-700"
                  disabled={creatingTable || isBulkCreatingTables}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  {t('dashboard:qrCodes.actions.addTableButton', { defaultValue: 'Add table' })}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem
                  disabled={creatingTable}
                  onSelect={(event) => {
                    event.preventDefault();
                    setIsBulkCreateDialogOpen(false);
                    setIsCreateDialogOpen(true);
                  }}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  {t('dashboard:qrCodes.actions.addSingle', { defaultValue: 'Add single table' })}
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={isBulkCreatingTables}
                  onSelect={(event) => {
                    event.preventDefault();
                    setIsCreateDialogOpen(false);
                    setIsBulkCreateDialogOpen(true);
                  }}
                >
                  <ListPlus className="mr-2 h-4 w-4" />
                  {t('dashboard:qrCodes.actions.addMultiple', { defaultValue: 'Add multiple tables' })}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {metrics.map((metric) => {
            const Icon = metric.icon;
            return (
              <Card
                key={metric.label}
                className="relative overflow-hidden border border-border bg-card shadow-sm transition-all hover:shadow-lg"
              >
                <div className={cn('absolute inset-0 bg-gradient-to-br opacity-60', metric.gradient, metric.gradientDark)} />
                <CardContent className="relative flex flex-col gap-3 p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm text-muted-foreground">{metric.label}</p>
                      <p className="text-2xl font-semibold text-foreground">{metric.value}</p>
                    </div>
                    <div className={cn('rounded-lg p-2', metric.iconBg, metric.iconBgDark)}>
                      <Icon className={`h-5 w-5 ${metric.iconColor}`} />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">{metric.helper}</p>
                  <div className="flex items-center gap-1.5 text-xs">
                    <TrendingUp className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                    <span className={`font-medium ${metric.trendColor}`}>{metric.trend}</span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <Card className="border border-border bg-card shadow-sm">
          <CardHeader className="flex flex-col gap-4 border-b border-border pb-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle className="flex items-center gap-2 text-xl text-foreground">
                <QrCode className="h-5 w-5 text-emerald-600" />
                QR Code Directory
              </CardTitle>
              <div className="flex flex-wrap items-center gap-2">
                <Badge className="rounded-full border-border bg-muted/60 text-xs text-muted-foreground" variant="outline">
                  {filteredTables.length} shown
                </Badge>
                <Badge
                  variant="outline"
                  className={
                    cacheStatus === 'cache'
                      ? 'rounded-full border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300'
                      : 'rounded-full border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                  }
                >
                  {cacheStatus === 'cache' ? 'Offline snapshot' : 'Live data'}
                </Badge>
              </div>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex w-full flex-col gap-3 sm:flex-row">
                <div className="relative flex-1">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder="Search by table, QR ID, or location"
                    className="pl-9"
                  />
                </div>
                <div className="relative w-full sm:w-48">
                  <Filter className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Select
                    value={statusFilter}
                    onValueChange={(value) => setStatusFilter(value as 'all' | 'available' | 'reserved' | 'occupied')}
                  >
                    <SelectTrigger className="w-full pl-9">
                      <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All status</SelectItem>
                      <SelectItem value="available">Available</SelectItem>
                      <SelectItem value="reserved">Reserved</SelectItem>
                      <SelectItem value="occupied">In service</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span>
                  Showing {filteredTables.length} of {tables.length}
                </span>
                {lastSyncedLabel && <span className="hidden sm:inline">- Last synced {lastSyncedLabel}</span>}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6 p-6">
            {filteredTables.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border bg-muted/10 px-6 py-16 text-center">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                  <AlertCircle className="h-6 w-6 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-medium text-foreground">No QR codes found</h3>
                <p className="mt-2 max-w-md text-sm text-muted-foreground">
                  {searchTerm || statusFilter !== 'all'
                    ? 'No tables match the current search or filter. Adjust your filters to try again.'
                    : 'Get started by adding your first table and generating its QR code.'}
                </p>
                {!searchTerm && statusFilter === 'all' && (
                  <Button
                    className="mt-6 bg-emerald-600 text-white shadow-lg shadow-emerald-500/25 hover:bg-emerald-700"
                    onClick={() => setIsCreateDialogOpen(true)}
                    disabled={creatingTable}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add first table
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
                {filteredTables.map((table) => {
                  const assets = generatedAssets[table.id];
                  const { displayLabel: tableLabel } = getTableDisplayMetadata(table);
                  const deepLink = assets?.deepLink || computeDeepLink(table, { demo: false });
                  const qrPreview = assets?.pngDataUrl || table.qrPngDataUrl;
                  const normalizedLocationName = table.location ? table.location.trim().toLowerCase() : null;
                  const orderingStatus =
                    (table.locationId ? locationOrderingById.get(table.locationId) : undefined) ??
                    (normalizedLocationName ? locationOrderingByName.get(normalizedLocationName) : undefined) ??
                    null;
                  const statusInfo = statusVisuals(table.status, orderingStatus, t);
                  const StatusIcon = statusInfo.icon;
                  const tableStats = qrMetricsSummary.statsByTable.get(table.id);
                  const scansCount = tableStats?.scansCount ?? 0;
                  const ordersCount = tableStats?.ordersCount ?? 0;
                  const revenueCents = tableStats?.revenueCents ?? 0;
                  const revenueDisplay = formatCurrency(revenueCents, currencyCode, { isMinorUnit: true });
                  const lastActivityIso = tableStats?.lastActivityAt ?? table.lastOrderAt ?? null;
                  const lastScanLabel = lastActivityIso
                    ? formatDistanceToNow(new Date(lastActivityIso), { addSuffix: true })
                    : 'No scans yet';
                  const isGeneratingThis = generatingTableId === table.id;
                  const isReleasingThis = releasingTableId === table.id;
                  const isActive = table.status === 'OCCUPIED';
                  const pendingCashCents = Math.max(0, table.pendingCashTotalCents ?? 0);
                  const hasPendingPayment = Boolean(table.hasPendingCash || pendingCashCents > 0);
                  const pendingPaymentLabel =
                    pendingCashCents > 0
                      ? formatCurrency(pendingCashCents, currencyCode, { isMinorUnit: true })
                      : null;
                  const pendingPaymentDescription = hasPendingPayment
                    ? pendingPaymentLabel
                      ? t('dashboard:qrCodes.pendingPayments.description', {
                          defaultValue: 'Collect {{amount}} before releasing this table.',
                          amount: pendingPaymentLabel,
                        })
                      : t('dashboard:qrCodes.pendingPayments.descriptionUnknown', {
                          defaultValue: 'Collect outstanding payments before releasing this table.',
                        })
                    : null;
                  const markReadyDisabled = isReleasingThis || hasPendingPayment;
                  const statusIndicatorClass =
                    table.status === 'OCCUPIED'
                      ? 'bg-gradient-to-r from-emerald-500 to-emerald-600'
                      : table.status === 'RESERVED'
                      ? 'bg-gradient-to-r from-amber-500 to-amber-600'
                      : 'bg-gradient-to-r from-slate-400 to-slate-500 dark:from-slate-600 dark:to-slate-700';
                  return (
                    <Card
                      key={table.id}
                      className="group relative overflow-hidden border border-border bg-card/90 shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl"
                      data-testid="qr-table-card"
                      data-table-id={table.id}
                    >
                      <div className={`absolute inset-x-0 top-0 h-1 ${statusIndicatorClass}`} />
                      <CardContent className="space-y-6 p-5 pt-6">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="mb-1 flex items-center gap-2">
                              <h3 className="text-lg font-semibold text-foreground">Table {tableLabel}</h3>
                              {isActive ? (
                                <Wifi className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                              ) : (
                                <WifiOff className="h-3.5 w-3.5 text-muted-foreground" />
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">{table.location ?? 'Unassigned location'}</p>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <Badge
                              variant="outline"
                              className={`flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium ${statusInfo.badgeClass}`}
                            >
                              <StatusIcon className="h-3.5 w-3.5" />
                              {statusInfo.label}
                            </Badge>
                            {hasPendingPayment && (
                              <Badge
                                variant="destructive"
                                className="flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium"
                              >
                                <AlertTriangle className="h-3.5 w-3.5" />
                                {t('dashboard:qrCodes.pendingPayments.badge', {
                                  defaultValue: 'Pending payment',
                                })}
                              </Badge>
                            )}
                          </div>
                        </div>

                        <div className="relative group/qr">
                          <div className="flex items-center justify-center rounded-xl border border-border bg-gradient-to-br from-gray-50 to-gray-100 p-6 transition-all duration-300 group-hover/qr:shadow-lg dark:from-gray-800 dark:to-gray-900">
                            {qrPreview ? (
                              <img
                                src={qrPreview}
                                alt={`QR code for table ${tableLabel}`}
                                className="h-36 w-36 rounded-md border border-border bg-white object-contain shadow-sm"
                                data-testid="qr-table-image"
                                data-table-id={table.id}
                              />
                            ) : (
                              <div className="flex h-36 w-36 items-center justify-center rounded-md border border-dashed border-border text-muted-foreground">
                                <QrCode className="h-10 w-10" />
                              </div>
                            )}
                          </div>
                          <div className="absolute inset-0 rounded-xl bg-gradient-to-t from-gray-900/50 to-transparent opacity-0 transition-opacity group-hover/qr:opacity-100" />
                        </div>

                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div className="rounded-lg border border-border bg-muted/50 p-3">
                            <div className="mb-1 flex items-center gap-2">
                              <BarChart3 className="h-3.5 w-3.5 text-muted-foreground" />
                              <span className="text-xs text-muted-foreground">Scans</span>
                            </div>
                            <p className="font-medium text-foreground">{formatNumber(scansCount)}</p>
                          </div>
                          <div className="rounded-lg border border-border bg-muted/50 p-3">
                            <div className="mb-1 flex items-center gap-2">
                              <QrCode className="h-3.5 w-3.5 text-muted-foreground" />
                              <span className="text-xs text-muted-foreground">Orders</span>
                            </div>
                            <p className="font-medium text-foreground">{formatNumber(ordersCount)}</p>
                          </div>
                          <div
                            className={cn(
                              'rounded-lg border bg-emerald-500/5 p-3',
                              'border-emerald-500/20 dark:border-emerald-500/40 dark:bg-emerald-500/15',
                            )}
                          >
                            <div className="mb-1 flex items-center gap-2">
                              <DollarSign className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                              <span className="text-xs text-emerald-600 dark:text-emerald-400">Revenue</span>
                            </div>
                            <p className="font-medium text-emerald-700 dark:text-emerald-300">{revenueDisplay}</p>
                          </div>
                          <div className="rounded-lg border border-border bg-muted/50 p-3">
                            <div className="mb-1 flex items-center gap-2">
                              <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                              <span className="text-xs text-muted-foreground">Last scan</span>
                            </div>
                            <p className="text-xs text-foreground">{lastScanLabel}</p>
                          </div>
                        </div>

                        {pendingPaymentDescription && (
                          <div className="rounded-lg border border-amber-500/40 bg-amber-50/70 p-3 text-xs text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100">
                            <div className="flex items-center gap-2 font-semibold">
                              <AlertTriangle className="h-3.5 w-3.5" />
                              {t('dashboard:qrCodes.pendingPayments.badge', {
                                defaultValue: 'Pending payment',
                              })}
                            </div>
                            <p className="mt-1 text-amber-800 dark:text-amber-100/80">{pendingPaymentDescription}</p>
                          </div>
                        )}

                        <div className="space-y-2 text-sm">
                          <div className="flex gap-2">
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="flex-1 border-border bg-card text-foreground hover:bg-accent"
                                >
                                  <Eye className="mr-1.5 h-3.5 w-3.5" />
                                  Preview
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="max-w-3xl overflow-hidden p-0">
                                <DialogHeader className="px-6 pt-6 pb-4">
                                  <DialogTitle className="flex items-center gap-2 text-foreground">
                                    <ExternalLink className="h-4 w-4" />
                                    Customer Menu - Table {tableLabel}
                                  </DialogTitle>
                                </DialogHeader>
                                <div className="h-[70vh] w-full border-t border-border">
                                  {deepLink ? (
                                    <iframe
                                      src={deepLink}
                                      title={`Customer menu for table ${tableLabel}`}
                                      className="h-full w-full"
                                    />
                                  ) : (
                                    <div className="flex h-full items-center justify-center bg-muted px-6 text-center text-sm text-muted-foreground">
                                      Generate the QR link to preview the customer menu.
                                    </div>
                                  )}
                                </div>
                              </DialogContent>
                            </Dialog>
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1 border-border bg-card text-foreground hover:bg-accent"
                              onClick={() => handleCopyLink(table)}
                            >
                              {copiedTableId === table.id ? (
                                <>
                                  <Check className="mr-1.5 h-3.5 w-3.5" />
                                  Copied
                                </>
                              ) : (
                                <>
                                  <Copy className="mr-1.5 h-3.5 w-3.5" />
                                  Copy URL
                                </>
                              )}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-border bg-card text-foreground hover:bg-accent"
                              onClick={() => handleDownload(table)}
                              disabled={isGeneratingThis}
                            >
                              <Download className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1 border-border bg-card text-foreground hover:bg-accent"
                              onClick={() => handleGenerate(table)}
                              disabled={isGeneratingThis || isReleasingThis}
                            >
                              <RefreshCcw className={`mr-1.5 h-3.5 w-3.5 ${isGeneratingThis ? 'animate-spin' : ''}`} />
                              Regenerate
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1 border-border bg-card text-foreground hover:bg-accent"
                              onClick={() => handleOpenOnPhone(table)}
                            >
                              <ArrowUpRight className="mr-1.5 h-3.5 w-3.5" />
                              Open on device
                            </Button>
                            {(table.status === 'OCCUPIED' || table.status === 'RESERVED') && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="flex-1 border-border bg-card text-foreground hover:bg-accent"
                                onClick={() => handleMarkReady(table)}
                                disabled={markReadyDisabled}
                                title={
                                  hasPendingPayment && pendingPaymentDescription
                                    ? pendingPaymentDescription
                                    : undefined
                                }
                              >
                                {isReleasingThis ? (
                                  <>
                                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                                    Marking...
                                  </>
                                ) : (
                                  <>
                                    <BadgeCheck className="mr-1.5 h-3.5 w-3.5" />
                                    Mark ready
                                  </>
                                )}
                              </Button>
                            )}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="icon"
                                  className="border-border bg-card text-foreground hover:bg-accent"
                                  disabled={isUpdatingTable || isReleasingThis}
                                  aria-label={t('dashboard:qrCodes.actions.manageTable', {
                                    defaultValue: 'Edit or delete table',
                                  })}
                                  title={t('dashboard:qrCodes.actions.manageTable', {
                                    defaultValue: 'Edit or delete table',
                                  })}
                                >
                                  <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                                  <span className="sr-only">
                                    {t('dashboard:qrCodes.actions.manageTable', {
                                      defaultValue: 'Edit or delete table',
                                    })}
                                  </span>
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-48">
                                <DropdownMenuItem
                                  disabled={isUpdatingTable || isReleasingThis}
                                  onSelect={(event) => {
                                    event.preventDefault();
                                    handleOpenEditDialog(table);
                                  }}
                                >
                                  <Pencil className="mr-2 h-4 w-4" aria-hidden="true" />
                                  {t('dashboard:qrCodes.actions.edit', { defaultValue: 'Edit table' })}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  disabled={Boolean(deletingTableId)}
                                  onSelect={(event) => {
                                    event.preventDefault();
                                    handleRequestDelete(table);
                                  }}
                                  className="text-destructive focus:text-destructive"
                                >
                                  <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
                                  {t('dashboard:qrCodes.actions.delete', { defaultValue: 'Delete table' })}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  </>
);
};

export default QRCodesPageV3;
